// Pure helpers for the team lead tools (kept separate so they are unit tested).

export const VOICES = ['male', 'female'];
export const PROMPT_SETS = ['inbound', 'otp', 'ivr'];
export const OPTION_TYPES = [
  'extension',
  'queue',
  'ring_group',
  'voicemail',
  'sub_menu',
  'repeat',
];
export const EXIT_TYPES = [
  'hangup',
  'extension',
  'queue',
  'ring_group',
  'voicemail',
];
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const UPLOAD_ACCEPT = '.wav,.mp3,.m4a,audio/wav,audio/mpeg,audio/mp4';
export const SLOT_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** True only when both numbers are known and the balance is below the threshold. */
export const isLowBalance = (amount, threshold) => {
  if (amount === null || amount === undefined || amount === '') return false;
  if (threshold === null || threshold === undefined || threshold === '')
    return false;
  return Number(amount) < Number(threshold);
};

export const formatMoney = (amount, currency, locale = 'en-NG') => {
  if (amount === null || amount === undefined || amount === '') return '';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(Number(amount));
  } catch (error) {
    return `${currency} ${amount}`;
  }
};

/** "digit-7" -> { key: 'DIGIT', digit: '7' }; fixed slots map to i18n keys; IVR names stay as typed. */
export const slotLabel = slot => {
  const digit = /^digit-(\d)$/.exec(slot);
  if (digit) return { key: 'DIGIT', digit: digit[1] };
  const position = /^position-(\d)$/.exec(slot);
  if (position) return { key: 'POSITION', position: position[1] };
  const fixed = [
    'welcome',
    'unavailable',
    'after_hours',
    'recording_notice',
    'leave_message',
    'message_thanks',
    'queue_wait',
    'hold_music',
    'intro',
    'repeat',
    'goodbye',
  ];
  if (fixed.includes(slot)) return { key: slot.toUpperCase() };
  return { name: slot };
};

/** Turns a free-text prompt name into a valid IVR slot ("Main menu!" -> "main-menu"). */
export const toSlotName = text =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export const uploadProblem = file => {
  if (!file) return 'NO_FILE';
  if (file.size > MAX_UPLOAD_BYTES) return 'TOO_LARGE';
  const name = (file.name || '').toLowerCase();
  if (!/\.(wav|mp3|m4a)$/.test(name)) return 'WRONG_TYPE';
  return null;
};

export const emptyMenu = () => ({
  name: '',
  extension: '',
  greeting: '',
  short_greeting: '',
  invalid_sound: '',
  exit_sound: '',
  timeout_seconds: 3,
  max_failures: 3,
  max_timeouts: 3,
  digit_len: 1,
  enabled: true,
  description: '',
  exit: { type: 'hangup', target: '' },
  options: [{ digits: '1', type: 'extension', target: '', description: '' }],
});

/** Editor state from a saved Relay spec. */
export const menuFromSpec = spec => ({
  ...emptyMenu(),
  ...spec,
  short_greeting: spec.short_greeting || '',
  invalid_sound: spec.invalid_sound || '',
  exit_sound: spec.exit_sound || '',
  timeout_seconds: (spec.timeout_ms || 3000) / 1000,
  exit: { type: spec.exit?.type || 'hangup', target: spec.exit?.target || '' },
  options: (spec.options || []).map(option => ({
    ...option,
    target: option.target || '',
    description: option.description || '',
  })),
});

/** The body the API expects: milliseconds, no empty optional prompts, no target for repeat/hangup. */
export const menuPayload = menu => {
  const payload = {
    name: menu.name.trim(),
    extension: String(menu.extension).trim(),
    greeting: menu.greeting,
    timeout_ms: Math.round(Number(menu.timeout_seconds) * 1000),
    max_failures: Number(menu.max_failures),
    max_timeouts: Number(menu.max_timeouts),
    digit_len: Number(menu.digit_len),
    enabled: Boolean(menu.enabled),
    description: (menu.description || '').trim(),
    exit:
      menu.exit.type === 'hangup'
        ? { type: 'hangup' }
        : { type: menu.exit.type, target: menu.exit.target },
    options: menu.options.map(option => {
      const row = { digits: String(option.digits).trim(), type: option.type };
      if (option.type !== 'repeat') row.target = option.target;
      if (option.description) row.description = option.description.trim();
      return row;
    }),
  };
  ['short_greeting', 'invalid_sound', 'exit_sound'].forEach(field => {
    if (menu[field]) payload[field] = menu[field];
  });
  return payload;
};

/** Problems the team lead can fix before asking the server (i18n keys under IVR.PROBLEMS). */
export const menuProblems = menu => {
  const problems = [];
  if (!menu.name.trim()) problems.push({ key: 'NAME' });
  if (!/^\d{3,6}$/.test(String(menu.extension).trim()))
    problems.push({ key: 'EXTENSION' });
  if (!menu.greeting) problems.push({ key: 'GREETING' });
  if (!menu.options.length) problems.push({ key: 'NO_OPTIONS' });
  if (menu.options.length > 12) problems.push({ key: 'TOO_MANY' });
  const seen = new Set();
  menu.options.forEach((option, index) => {
    const n = index + 1;
    const digits = String(option.digits).trim();
    if (!/^[0-9*#]{1,5}$/.test(digits)) problems.push({ key: 'KEYS', n });
    else if (digits.length > Number(menu.digit_len))
      problems.push({ key: 'KEY_LENGTH', n });
    if (seen.has(digits)) problems.push({ key: 'DUPLICATE', n, digits });
    seen.add(digits);
    if (option.type !== 'repeat' && !option.target)
      problems.push({ key: 'TARGET', n });
  });
  if (menu.exit.type !== 'hangup' && !menu.exit.target)
    problems.push({ key: 'EXIT' });
  const seconds = Number(menu.timeout_seconds);
  if (!(seconds >= 1 && seconds <= 30)) problems.push({ key: 'TIMEOUT' });
  return problems;
};

/** Versions of a slot recorded in a given voice, newest first. */
export const versionsFor = (slot, voice) =>
  (slot.versions || []).filter(version => version.voice === voice);

// Premium-rate blocklist prefixes from what a team lead typed: country codes
// and digits without "+", separated by commas, spaces or new lines.
export const parsePrefixes = text =>
  [
    ...new Set(
      String(text || '')
        .split(/[\s,;]+/)
        .map(prefix => prefix.replace(/^\+/, ''))
        .filter(prefix => /^[1-9]\d{0,6}$/.test(prefix))
    ),
  ].sort();
